import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();

const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});

export const TransferOrderWarehouseSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

export const TransferOrderLineItemSchema = z.object({
  id: UUIDSchema,
  lineNumber: z.int().nullable().optional(),
  productId: UUIDSchema.nullable().optional(),
  productVariantId: UUIDSchema.nullable().optional(),
  productName: z.string().nullable().optional(),
  productSku: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  quantity: DecimalStringSchema,
  shippedQuantity: DecimalStringSchema.nullable().optional(),
  unitOfMeasure: z.string().nullable().optional(),
});

export const TransferOrderChargeSchema = z.object({
  id: UUIDSchema,
  amount: DecimalStringSchema,
  currencyCode: z.string(),
  categoryLabel: z.string().nullable().optional(),
});

export const TransferOrderSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  internalId: z.int(),
  transferNumber: z.string(),
  status: z.string(),
  shipmentId: UUIDSchema.nullable().optional(),
  sourceWarehouseId: UUIDSchema.nullable().optional(),
  destinationWarehouseId: UUIDSchema.nullable().optional(),
  sourceWarehouse: TransferOrderWarehouseSchema.nullable().optional(),
  destinationWarehouse: TransferOrderWarehouseSchema.nullable().optional(),
  orderDate: z.string(),
  shipDate: z.string().nullable().optional(),
  deliveryDate: z.string().nullable().optional(),
  grossWeight: z.number().nullable().optional(),
  pallets: z.number().nullable().optional(),
  quantity: DecimalStringSchema.nullable().optional(),
  currencyCode: z.string().nullable().optional(),
  notes: z.array(z.string()).nullable().optional(),
  originId: z.string().nullable().optional(),
  originSystem: z.string().nullable().optional(),
  originEntity: z.string().nullable().optional(),
});

export const GetTransferOrderInputSchema = z.object({
  id: UUIDSchema,
});

export const GetTransferOrderResponseSchema = z.object({
  transferOrder: TransferOrderSchema.nullable(),
  lineItems: z.array(TransferOrderLineItemSchema),
  charges: z.array(TransferOrderChargeSchema),
});

export const transferOrdersContracts = {
  get: oc
    .input(GetTransferOrderInputSchema)
    .output(GetTransferOrderResponseSchema)
    .route({
      method: "GET",
    }),
} as const;

export type TransferOrder = z.infer<typeof TransferOrderSchema>;
export type TransferOrderLineItem = z.infer<typeof TransferOrderLineItemSchema>;
export type TransferOrderCharge = z.infer<typeof TransferOrderChargeSchema>;
export type TransferOrderWarehouse = z.infer<
  typeof TransferOrderWarehouseSchema
>;
export type GetTransferOrderInput = z.infer<typeof GetTransferOrderInputSchema>;
export type GetTransferOrderResponse = z.infer<
  typeof GetTransferOrderResponseSchema
>;
